'use client'

import * as React from 'react'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Copy,
  Grid2x2,
  GalleryHorizontal,
  LayoutGrid,
  Images,
  CircleDot,
  Clapperboard,
  PanelsTopLeft,
  Instagram,
  Music2,
  Hash,
  Star,
  Youtube,
  Upload,
  ShieldCheck,
  Loader2,
  Sparkles,
  ExternalLink,
  Lock,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { trackGenerateLead } from '@/lib/analytics/ga4'
import { TurnstileWidget, useTurnstileSiteKey } from '@/lib/turnstile'
import { LiveWidgetPreview } from '@/components/tools/widget/live-widget-preview'
import { SamplePreview } from '@/components/tools/widget/sample-preview'
import { ColorPicker } from '@/components/ui/color-picker'
import {
  DEFAULT_WIDGET_CONFIG,
  buildEmbedSnippet,
  appSignupUrl,
  type WidgetConfig,
  type WidgetView,
  type WidgetSource,
} from '@/lib/widget/embed'
import { demoPostsFor, type DemoPost, type Hotspot } from '@/lib/copy/demo-products'

// The flagship widget builder. A visitor composes a real Idukki widget
// config in three guided moves (source, layout, customise), watches a
// faithful client-rendered preview update live, then captures an embed
// or routes into the app. Mobile-first: the preview sits above the
// controls on small screens and becomes a sticky right rail on desktop.

type Step = 0 | 1 | 2 | 3

// The sanitized per-post shape the /api/widget/preview proxy returns.
// Mirrors the serverless endpoint's allow-list — no internal ids ever.
type PreviewPost = {
  mediaType: string
  mediaUrl: string
  thumbnail: string
  permalink: string
  username: string
  caption: string
}

type SourceMeta = {
  value: WidgetSource
  label: string
  icon: React.ComponentType<{ className?: string }>
  blurb: string
  demoSlug: string
  /** Prompt + placeholder for the "what should we pull" input. `null`
   *  inputLabel means the source has nothing to type (e.g. Upload). */
  inputLabel: string | null
  placeholder?: string
  note?: string
}

const SOURCES: SourceMeta[] = [
  { value: 'instagram', label: 'Instagram', icon: Instagram, blurb: 'Feed, hashtag or tagged posts', demoSlug: 'fashion', inputLabel: 'Instagram handle or hashtag', placeholder: '@yourbrand or #campaign', note: 'Your feed, a hashtag, or posts you’re tagged in.' },
  { value: 'tiktok', label: 'TikTok', icon: Music2, blurb: 'Short-form video, autoplayed muted', demoSlug: 'beauty', inputLabel: 'TikTok handle or hashtag', placeholder: '@yourbrand or #campaign' },
  { value: 'hashtag', label: 'Hashtag', icon: Hash, blurb: 'Aggregate a campaign tag across channels', demoSlug: 'sport', inputLabel: 'Campaign hashtag', placeholder: '#yourcampaign', note: 'We aggregate this tag across Instagram, TikTok, X and Threads.' },
  { value: 'reviews', label: 'Reviews', icon: Star, blurb: 'Google, Trustpilot, Feefo photo reviews', demoSlug: 'home', inputLabel: 'Business name or review link', placeholder: 'Acme Co · or a Google/Trustpilot URL' },
  { value: 'youtube', label: 'YouTube', icon: Youtube, blurb: 'Long-form and Shorts, tagged to SKUs', demoSlug: 'electronics', inputLabel: 'YouTube channel or @handle', placeholder: '@yourchannel' },
  { value: 'upload', label: 'Upload', icon: Upload, blurb: 'Your own first-party clips and photos', demoSlug: 'jewel', inputLabel: null, note: 'You’ll upload your own clips and photos in the app.' },
]

// The free builder lets a visitor compose against Instagram + TikTok
// only; the rest (Reviews, YouTube, Upload, Hashtag, and the channels
// listed in the teaser) unlock after signup. The full SOURCES array, the
// WidgetSource type and the provision allow-list stay intact because the
// app and the deep-link layer still reference every value.
const SELECTABLE_SOURCES = ['instagram', 'tiktok'] as const satisfies readonly WidgetSource[]
const SELECTABLE_SOURCE_METAS: SourceMeta[] = SOURCES.filter((s) =>
  (SELECTABLE_SOURCES as readonly WidgetSource[]).includes(s.value),
)

const LAYOUTS: { value: WidgetView; label: string; icon: React.ComponentType<{ className?: string }>; blurb: string }[] = [
  { value: 'grid', label: 'Grid', icon: Grid2x2, blurb: 'Clean, even tiles. Tag every SKU.' },
  { value: 'carousel', label: 'Carousel', icon: GalleryHorizontal, blurb: 'Swipeable rail above the fold.' },
  { value: 'masonry', label: 'Masonry', icon: LayoutGrid, blurb: 'Mixed ratios packed with no gaps.' },
  { value: 'collage', label: 'Collage', icon: Images, blurb: 'A feature shot anchoring a cluster.' },
  { value: 'stories', label: 'Stories', icon: CircleDot, blurb: 'Tappable circles, native to mobile.' },
  { value: 'reelCarousel', label: 'Reels', icon: Clapperboard, blurb: 'Vertical video tiles, autoplay.' },
  { value: 'wall2', label: 'Social wall', icon: PanelsTopLeft, blurb: 'A live, brand-safe feed wall.' },
]

const STEP_LABELS = ['Source', 'Layout', 'Customise', 'Go live'] as const

function usePrefersReducedMotion() {
  const [reduced, setReduced] = React.useState(false)
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const onChange = () => setReduced(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return reduced
}

function isView(v: string | null): v is WidgetView {
  return !!v && LAYOUTS.some((l) => l.value === v)
}
function isSource(v: string | null): v is WidgetSource {
  // Only the selectable sources may be preselected from a deep link, so
  // ?source=reviews (or any locked source) is ignored and the default holds.
  return !!v && (SELECTABLE_SOURCES as readonly string[]).includes(v)
}

export function WidgetBuilder({ demoGuid }: { demoGuid?: string }) {
  const reducedMotion = usePrefersReducedMotion()

  // Read deep-link params once on mount so /tools/widget-builder?view=…&source=…
  // (from the templates gallery / channel pages) preselects the choice.
  const [config, setConfig] = React.useState<WidgetConfig>(DEFAULT_WIDGET_CONFIG)
  const [step, setStep] = React.useState<Step>(0)
  const [showReal, setShowReal] = React.useState(false)

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const view = params.get('view')
    const source = params.get('source')
    setConfig((c) => {
      // Normalize any locked source (e.g. a stale default) back to instagram
      // so the source step only ever lands on a selectable choice.
      const lockedSource = !(SELECTABLE_SOURCES as readonly string[]).includes(c.source)
      return {
        ...c,
        ...(lockedSource ? { source: 'instagram' as WidgetSource } : {}),
        ...(isView(view) ? { view } : {}),
        ...(isSource(source) ? { source } : {}),
      }
    })
    // If they arrived with a layout already chosen, jump them past the
    // source step so the deep-link feels like it landed somewhere.
    if (isView(view)) setStep(1)
  }, [])

  const set = <K extends keyof WidgetConfig>(key: K, value: WidgetConfig[K]) =>
    setConfig((c) => ({ ...c, [key]: value }))

  const next = () => setStep((s) => (Math.min(3, s + 1) as Step))
  const back = () => setStep((s) => (Math.max(0, s - 1) as Step))

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:items-start">
      {/* Preview — first in DOM so mobile sees it up top; sticky rail on lg. */}
      <div className="order-1 lg:order-2 lg:sticky lg:top-24">
        <PreviewPanel
          config={config}
          reducedMotion={reducedMotion}
          demoGuid={demoGuid}
          showReal={showReal}
          onToggleReal={setShowReal}
        />
      </div>

      {/* Controls */}
      <div className="order-2 lg:order-1">
        <Stepper step={step} onJump={setStep} />

        <div className="mt-6 rounded-2xl border bg-card p-5 sm:p-6">
          {step === 0 && (() => {
            const selectedSource = SOURCES.find((s) => s.value === config.source)
            return (
            <Fieldset legend="Where should the UGC come from?" hint="Pick your main source and tell us what to pull. You connect it securely in-app.">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Content source">
                {SELECTABLE_SOURCE_METAS.map((s) => (
                  <SelectCard
                    key={s.value}
                    selected={config.source === s.value}
                    onSelect={() => { set('source', s.value); set('sourceHandle', '') }}
                    icon={s.icon}
                    label={s.label}
                    blurb={s.blurb}
                  />
                ))}
              </div>

              {selectedSource?.inputLabel ? (
                <div className="mt-4">
                  <label className="text-sm">
                    <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {selectedSource.inputLabel}
                    </span>
                    <Input
                      type="text"
                      value={config.sourceHandle ?? ''}
                      onChange={(e) => set('sourceHandle', e.target.value.slice(0, 80))}
                      placeholder={selectedSource.placeholder}
                      className="mt-1"
                      autoComplete="off"
                      aria-describedby="source-note"
                    />
                  </label>
                  {selectedSource.note && (
                    <p id="source-note" className="mt-1.5 text-xs text-muted-foreground">{selectedSource.note}</p>
                  )}
                </div>
              ) : selectedSource?.note ? (
                <p className="mt-4 text-xs text-muted-foreground">{selectedSource.note}</p>
              ) : null}

              <LockTeaser>More sources (Reviews, YouTube, Upload, Hashtag, LinkedIn, Threads, X, Google Reviews and more) unlock once you sign up.</LockTeaser>
            </Fieldset>
            )
          })()}

          {step === 1 && (
            <Fieldset legend="Pick a layout" hint="The preview is exact: every choice here renders live on the right.">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Layout">
                {LAYOUTS.map((l) => (
                  <SelectCard
                    key={l.value}
                    selected={config.view === l.value}
                    onSelect={() => set('view', l.value)}
                    icon={l.icon}
                    label={l.label}
                    blurb={l.blurb}
                  />
                ))}
              </div>
              <LockTeaser>More layouts (lookbook, shoppable video, live wall and more) unlock once you sign up.</LockTeaser>
            </Fieldset>
          )}

          {step === 2 && (
            <CustomiseStep config={config} set={set} />
          )}

          {step === 3 && (
            <GoLiveStep config={config} />
          )}

          {step < 3 && (
            <div className="mt-6 flex items-center justify-between gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={back}
                disabled={step === 0}
                className={cn(step === 0 && 'invisible')}
              >
                <ArrowLeft className="mr-1.5 h-4 w-4" aria-hidden />
                Back
              </Button>
              <Button type="button" onClick={next}>
                {step === 2 ? 'Get my embed' : 'Continue'}
                <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden />
              </Button>
            </div>
          )}
        </div>

        <ValueRecap config={config} />
      </div>
    </div>
  )
}

// ── Stepper ──────────────────────────────────────────────────────────
function Stepper({ step, onJump }: { step: Step; onJump: (s: Step) => void }) {
  return (
    <ol className="flex items-center gap-2" aria-label="Builder progress">
      {STEP_LABELS.map((label, i) => {
        const done = i < step
        const active = i === step
        return (
          <li key={label} className="flex flex-1 items-center gap-2">
            <button
              type="button"
              onClick={() => onJump(i as Step)}
              disabled={i > step}
              aria-current={active ? 'step' : undefined}
              className={cn(
                'flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                active && 'border-primary bg-primary/5',
                done && 'border-primary/40',
                i > step && 'opacity-60',
              )}
            >
              <span
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                  done ? 'bg-primary text-primary-foreground' : active ? 'border border-primary text-primary' : 'border text-muted-foreground',
                )}
                aria-hidden
              >
                {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className={cn('hidden text-xs font-medium sm:inline', active ? 'text-foreground' : 'text-muted-foreground')}>
                {label}
              </span>
            </button>
          </li>
        )
      })}
    </ol>
  )
}

// A small "unlocks on signup" teaser, used under the source + layout
// steps to signal the free build is a slice of what the app offers.
function LockTeaser({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-4 inline-flex items-start gap-2 rounded-lg bg-secondary/60 px-3 py-2 text-xs text-muted-foreground">
      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
      <span>{children}</span>
    </p>
  )
}

function Fieldset({ legend, hint, children }: { legend: string; hint?: string; children: React.ReactNode }) {
  return (
    <fieldset className="m-0 border-0 p-0">
      <legend className="font-display text-lg font-semibold tracking-tight text-foreground">{legend}</legend>
      {hint && <p className="mt-1.5 text-sm text-muted-foreground">{hint}</p>}
      <div className="mt-4">{children}</div>
    </fieldset>
  )
}

function SelectCard({
  selected,
  onSelect,
  icon: Icon,
  label,
  blurb,
}: {
  selected: boolean
  onSelect: () => void
  icon: React.ComponentType<{ className?: string }>
  label: string
  blurb: string
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'group relative flex h-full flex-col gap-1.5 rounded-xl border bg-background p-3 text-left transition-[transform,border-color,box-shadow] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-safe:hover:-translate-y-0.5',
        selected ? 'border-primary shadow-[0_8px_24px_-12px_hsl(var(--primary)/0.5)]' : 'hover:border-primary/40',
      )}
    >
      <span className="flex items-center justify-between">
        <Icon className={cn('h-5 w-5', selected ? 'text-primary' : 'text-muted-foreground')} aria-hidden />
        {selected && <Check className="h-4 w-4 text-primary" aria-hidden />}
      </span>
      <span className="text-sm font-semibold text-foreground">{label}</span>
      <span className="text-xs leading-snug text-muted-foreground">{blurb}</span>
    </button>
  )
}

// ── Customise step ───────────────────────────────────────────────────
function CustomiseStep({
  config,
  set,
}: {
  config: WidgetConfig
  set: <K extends keyof WidgetConfig>(key: K, value: WidgetConfig[K]) => void
}) {
  return (
    <Fieldset legend="Customise the look" hint="Tune the grid and the accent. The preview updates as you go.">
      <div className="grid gap-5">
        <SliderRow
          id="columns"
          label="Desktop columns"
          min={1}
          max={8}
          value={config.columns}
          onChange={(v) => set('columns', v)}
        />
        <SliderRow
          id="mobileColumns"
          label="Mobile columns"
          min={1}
          max={4}
          value={config.mobileColumns}
          onChange={(v) => set('mobileColumns', v)}
        />
        <SliderRow
          id="spacing"
          label="Tile spacing"
          min={0}
          max={40}
          value={config.spacing}
          unit="px"
          onChange={(v) => set('spacing', v)}
        />

        <div className="flex flex-wrap items-center gap-4">
          <span className="text-sm font-medium text-foreground">Accent colour</span>
          <ColorPicker value={config.accent} onChange={(hex) => set('accent', hex)} ariaLabel="Accent colour" />
        </div>

        <label className="text-sm">
          <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">Widget name</span>
          <Input
            type="text"
            value={config.name}
            onChange={(e) => set('name', e.target.value.slice(0, 80))}
            placeholder="My UGC gallery"
            className="mt-1"
          />
        </label>
      </div>
    </Fieldset>
  )
}

function SliderRow({
  id,
  label,
  min,
  max,
  value,
  unit,
  onChange,
}: {
  id: string
  label: string
  min: number
  max: number
  value: number
  unit?: string
  onChange: (v: number) => void
}) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-sm font-medium text-foreground">
          {label}
        </label>
        <span className="text-sm tabular-nums text-muted-foreground">
          {value}
          {unit ?? ''}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-2 h-2 w-full cursor-pointer appearance-none rounded-full bg-secondary accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
    </div>
  )
}

// Adapt the sanitized posts the preview proxy returns into the DemoPost
// shape the SamplePreview renderer already consumes, so only the data
// source changes — every layout / columns / spacing / accent rule is
// reused untouched. Real posts carry no product tags, so to demonstrate
// shoppability (founder ask) we attach *sample* hotspots — drawn from
// the source's documented demo products — to the first 1-2 tiles only.
function realPostsToDemoPosts(real: PreviewPost[], demoSlug: string): DemoPost[] {
  // Pull the demo posts for this source purely to borrow their hotspot
  // → product mappings (documented sample products), not their images.
  const sampleHotspots = demoPostsFor(demoSlug)
    .flatMap((p) => p.hotspots)
    .filter((h, i, arr) => arr.findIndex((x) => x.productId === h.productId) === i)

  return real.map((post, i) => {
    // Only the first two tiles get a sample pin, mirroring how a real
    // store tags a couple of hero posts rather than every photo.
    const hotspots: Hotspot[] =
      i < 2 && sampleHotspots[i]
        ? [{ x: i === 0 ? 42 : 58, y: i === 0 ? 52 : 46, productId: sampleHotspots[i].productId }]
        : []
    return {
      image: post.thumbnail || post.mediaUrl,
      caption: post.caption || `Post by ${post.username || 'a creator'}`,
      user: post.username ? (post.username.startsWith('@') ? post.username : `@${post.username}`) : '@creator',
      likes: 0,
      comments: 0,
      hotspots,
      videoSrc: post.mediaType?.toLowerCase().includes('video') ? post.mediaUrl || undefined : undefined,
      originalUrl: post.permalink || undefined,
    }
  })
}

// ── Live preview panel (faithful client render + real-data fetch) ──────
function PreviewPanel({
  config,
  reducedMotion,
  demoGuid,
  showReal,
  onToggleReal,
}: {
  config: WidgetConfig
  reducedMotion: boolean
  demoGuid?: string
  showReal: boolean
  onToggleReal: (v: boolean) => void
}) {
  const sourceMeta = SOURCES.find((s) => s.value === config.source)
  const SourceIcon = sourceMeta?.icon
  const samplePosts = React.useMemo(() => demoPostsFor(sourceMeta?.demoSlug ?? 'fashion'), [sourceMeta?.demoSlug])

  const live = useLivePreview(config)
  // The live preview is only offered for the IG / TikTok sources the
  // serverless endpoint supports; the type already restricts to those
  // two, but guard anyway so any future locked source stays sample-only.
  const canGoLive = config.source === 'instagram' || config.source === 'tiktok'
  const handle = (config.sourceHandle ?? '').trim()

  // Adapt real posts (when we have them) into the DemoPost shape the
  // SamplePreview renderer consumes. Falls back to sample posts on
  // error / empty / no-key — real posts are strictly additive.
  const livePosts = React.useMemo(
    () => (live.posts.length > 0 ? realPostsToDemoPosts(live.posts, sourceMeta?.demoSlug ?? 'fashion') : []),
    [live.posts, sourceMeta?.demoSlug],
  )
  const showingLive = livePosts.length > 0 && !showReal
  const posts = showingLive ? livePosts : samplePosts

  return (
    <div className="rounded-2xl border bg-card p-4 sm:p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        {showingLive ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-primary">
            <span className="relative flex h-1.5 w-1.5">
              <span className={cn('absolute inline-flex h-1.5 w-1.5 rounded-full bg-primary/70', !reducedMotion && 'animate-ping')} />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
            </span>
            Live · #{live.tag}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-primary">
            <span className="relative flex h-1.5 w-1.5">
              <span className={cn('absolute inline-flex h-1.5 w-1.5 rounded-full bg-primary/70', !reducedMotion && 'animate-ping')} />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
            </span>
            Live preview
          </span>
        )}
        {demoGuid && (
          <div className="inline-flex rounded-full border bg-background p-0.5" role="group" aria-label="Preview mode">
            <ToggleBtn active={!showReal} onClick={() => onToggleReal(false)}>
              Your config
            </ToggleBtn>
            <ToggleBtn active={showReal} onClick={() => onToggleReal(true)}>
              Real widget
            </ToggleBtn>
          </div>
        )}
      </div>

      {(handle || sourceMeta) && !showReal && (
        <p className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-[11px] text-muted-foreground">
          {SourceIcon && <SourceIcon className="h-3 w-3 text-foreground" aria-hidden />}
          {handle ? (
            <><span className="font-medium text-foreground">{handle}</span><span>· {sourceMeta?.label}</span></>
          ) : (
            <span>{sourceMeta?.label} preview</span>
          )}
        </p>
      )}

      {/* Inline live-preview gate: shown when the visitor has typed a
          handle/hashtag for an IG/TikTok source and we don't yet have
          real posts. Submitting fetches live content; everything else
          keeps the sample preview underneath untouched. */}
      {canGoLive && handle && !showReal && !showingLive && (
        <LivePreviewGate state={live} onFetch={live.fetchLive} handle={handle} />
      )}

      {showReal && demoGuid ? (
        <LiveWidgetPreview guid={demoGuid} title="Live Idukki widget preview" />
      ) : (
        <SamplePreview config={config} posts={posts} reducedMotion={reducedMotion} />
      )}

      <p className="mt-3 text-xs text-muted-foreground">
        {showingLive ? (
          <>Real posts for <span className="font-medium text-foreground">#{live.tag}</span> · sample product pins show how
          tagging looks. Your store&rsquo;s products tag in-app.</>
        ) : (
          <>Sample content for shape and styling. {demoGuid ? 'Toggle to a real rendered widget, or your' : 'Your'} real
          feed connects securely in-app.</>
        )}
      </p>
    </div>
  )
}

// ── Live preview fetch hook + inline gate ──────────────────────────────
type LivePreviewState = {
  status: 'idle' | 'submitting' | 'error'
  posts: PreviewPost[]
  tag: string
  message: string
  needsTurnstile: boolean
  token: string
  setToken: (t: string) => void
  fetchLive: () => void
}

function useLivePreview(config: WidgetConfig): LivePreviewState {
  const [status, setStatus] = React.useState<'idle' | 'submitting' | 'error'>('idle')
  const [posts, setPosts] = React.useState<PreviewPost[]>([])
  const [tag, setTag] = React.useState('')
  const [message, setMessage] = React.useState('')
  const [token, setToken] = React.useState('')
  const turnstileSiteKey = useTurnstileSiteKey()

  // Drop any fetched posts when the source or handle changes so the
  // preview never shows last source's content against a new query.
  const handle = (config.sourceHandle ?? '').trim()
  React.useEffect(() => {
    setPosts([])
    setStatus('idle')
    setMessage('')
  }, [config.source, handle])

  const fetchLive = React.useCallback(() => {
    const cleanTag = handle.replace(/^[#@]+/, '').trim().toLowerCase()
    if (!cleanTag) return
    setStatus('submitting')
    setMessage('')
    void (async () => {
      try {
        const res = await fetch('/api/widget/preview', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            source: config.source,
            hashtag: handle,
            company_url: '', // honeypot
            'cf-turnstile-response': token,
          }),
        })
        const json = (await res.json().catch(() => ({}))) as {
          ok?: boolean
          posts?: PreviewPost[]
          hashtag?: string
          error?: string
        }
        // Graceful fallback: any non-ok, empty, or 429 just keeps the
        // sample preview. We only flag an explicit message on a hard
        // client error so the visitor knows to retry — never a crash.
        if (!res.ok || !json?.ok) {
          setStatus(res.status === 429 ? 'error' : 'idle')
          setMessage(res.status === 429 ? json?.error || 'Try again in a moment.' : '')
          setPosts([])
          return
        }
        setTag(json.hashtag || cleanTag)
        setPosts(Array.isArray(json.posts) ? json.posts : [])
        setStatus('idle')
      } catch {
        // Network error → silently keep samples.
        setStatus('idle')
        setPosts([])
      }
    })()
  }, [config.source, handle, token])

  return {
    status,
    posts,
    tag,
    message,
    needsTurnstile: Boolean(turnstileSiteKey),
    token,
    setToken,
    fetchLive,
  }
}

function LivePreviewGate({
  state,
  onFetch,
  handle,
}: {
  state: LivePreviewState
  onFetch: () => void
  handle: string
}) {
  return (
    <div className="mb-3 rounded-xl border border-dashed bg-secondary/40 p-3">
      <p className="text-xs font-medium text-foreground">
        Pull real posts for <span className="text-primary">{handle}</span>
      </p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        We fetch a handful of public posts so the preview shows your actual content. Sample product pins stay on, so you
        can see how shopping looks.
      </p>
      {state.needsTurnstile && (
        <div className="mt-2.5 max-w-[300px]">
          <p className="mb-1.5 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3 w-3" aria-hidden /> Bot-protection
          </p>
          <TurnstileWidget size="compact" onToken={state.setToken} onExpire={() => state.setToken('')} />
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          onClick={onFetch}
          disabled={state.status === 'submitting' || (state.needsTurnstile && !state.token)}
        >
          {state.status === 'submitting' ? (
            <>
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden /> Fetching…
            </>
          ) : (
            <>
              <Sparkles className="mr-1.5 h-3.5 w-3.5" aria-hidden /> Show live posts
            </>
          )}
        </Button>
        <span className="text-[11px] text-muted-foreground">or keep the sample below</span>
      </div>
      {state.status === 'error' && state.message && (
        <p role="alert" className="mt-2 text-[11px] text-destructive">
          {state.message}
        </p>
      )}
    </div>
  )
}

function ToggleBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}

// ── Go-live step: email capture → provision → embed / signup ──────────
type Result =
  | { kind: 'embed'; guid: string; bguid?: string; embed: string; previewUrl: string }
  | { kind: 'signup'; signupUrl: string }

function GoLiveStep({ config }: { config: WidgetConfig }) {
  const [state, setState] = React.useState<'idle' | 'submitting' | 'error'>('idle')
  const [result, setResult] = React.useState<Result | null>(null)
  const [message, setMessage] = React.useState('')
  const [turnstileToken, setTurnstileToken] = React.useState('')
  const turnstileSiteKey = useTurnstileSiteKey()

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setState('submitting')
    setMessage('')
    const fd = new FormData(e.currentTarget)
    const payload = {
      config,
      email: String(fd.get('email') || ''),
      name: String(fd.get('name') || ''),
      source: config.source,
      company_url: String(fd.get('company_url') || ''), // honeypot
      'cf-turnstile-response': turnstileToken,
    }
    try {
      const res = await fetch('/api/widget/provision', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json?.ok) {
        setState('error')
        setMessage(json?.error || 'Something went wrong. Try again or email hi@idukki.io.')
        return
      }
      trackGenerateLead({ intent: 'try-tool', source: `widget-builder:${config.source}` })
      if (json.embed && json.guid) {
        setResult({ kind: 'embed', guid: json.guid, bguid: json.bguid, embed: json.embed, previewUrl: json.previewUrl })
      } else {
        setResult({ kind: 'signup', signupUrl: json.signupUrl || appSignupUrl({ source: config.source }) })
      }
      setState('idle')
    } catch {
      setState('error')
      setMessage('Network error. Try again or email hi@idukki.io.')
    }
  }

  if (result) {
    return (
      <div aria-live="polite">
        {result.kind === 'embed' ? (
          <EmbedResult result={result} config={config} />
        ) : (
          <SignupResult signupUrl={result.signupUrl} />
        )}
      </div>
    )
  }

  return (
    <Fieldset
      legend="Get your embed"
      hint="Composing and previewing is free here. To connect your real feed and publish, you'll finish in the app. The free embed runs to a generous impression cap."
    >
      <form onSubmit={onSubmit}>
        {/* Honeypot */}
        <div className="absolute left-[-9999px] top-[-9999px]" aria-hidden>
          <label>
            Company website
            <input type="text" name="company_url" tabIndex={-1} autoComplete="off" />
          </label>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">Email</span>
            <Input type="email" name="email" required placeholder="you@brand.com" className="mt-1" />
          </label>
          <label className="text-sm">
            <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">Name (optional)</span>
            <Input type="text" name="name" placeholder="Your name" className="mt-1" />
          </label>
        </div>

        {turnstileSiteKey && (
          <div className="mt-4 max-w-[320px]">
            <p className="mb-1.5 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <ShieldCheck className="h-3 w-3" aria-hidden /> Cloudflare bot-protection
            </p>
            <TurnstileWidget size="normal" onToken={setTurnstileToken} onExpire={() => setTurnstileToken('')} />
          </div>
        )}

        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">No spam. Unsubscribe anytime.</p>
          <Button type="submit" disabled={state === 'submitting' || (Boolean(turnstileSiteKey) && !turnstileToken)}>
            {state === 'submitting' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> Building…
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" aria-hidden /> Get my embed
              </>
            )}
          </Button>
        </div>
        {state === 'error' && message && (
          <p role="alert" className="mt-3 text-xs text-destructive">
            {message}
          </p>
        )}
      </form>
    </Fieldset>
  )
}

function EmbedResult({ result, config }: { result: Extract<Result, { kind: 'embed' }>; config: WidgetConfig }) {
  const [copied, setCopied] = React.useState(false)
  async function copy() {
    try {
      await navigator.clipboard.writeText(result.embed)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard blocked — the textarea is selectable as a fallback */
    }
  }
  return (
    <div>
      <p className="inline-flex items-center gap-2 font-display text-lg font-semibold text-foreground">
        <Check className="h-5 w-5 text-primary" aria-hidden /> Your widget is ready to embed
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        Paste this into any storefront or HTML page. It renders your {config.view} layout from collection{' '}
        <code className="rounded bg-secondary px-1 py-0.5 text-xs">{result.guid}</code>.
      </p>
      <div className="mt-4 rounded-xl border bg-background p-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Embed snippet</span>
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold text-foreground transition-colors duration-150 hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-primary" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <textarea
          readOnly
          rows={3}
          value={result.embed}
          aria-label="Widget embed snippet"
          className="w-full resize-none rounded-lg border bg-secondary/40 p-3 font-mono text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onFocus={(e) => e.currentTarget.select()}
        />
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <a
          href={result.previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold text-foreground transition-colors duration-150 hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Open hosted preview <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
        <Button asChild size="sm">
          <a href={appSignupUrl({ source: config.source, ref: 'widget-builder-embed' })}>
            Unlock full features in app <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden />
          </a>
        </Button>
      </div>
    </div>
  )
}

function SignupResult({ signupUrl }: { signupUrl: string }) {
  return (
    <div>
      <p className="inline-flex items-center gap-2 font-display text-lg font-semibold text-foreground">
        <Check className="h-5 w-5 text-primary" aria-hidden /> Saved. Let&rsquo;s publish it
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        Your layout is captured. Connect your real source (a quick OAuth) and go live in the app. It takes about four
        minutes, no credit card.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button asChild>
          <a href={signupUrl}>
            Continue in app to publish <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden />
          </a>
        </Button>
      </div>
    </div>
  )
}

// ── Value recap rail ─────────────────────────────────────────────────
function ValueRecap({ config }: { config: WidgetConfig }) {
  const sourceLabel = SOURCES.find((s) => s.value === config.source)?.label ?? config.source
  const layoutLabel = LAYOUTS.find((l) => l.value === config.view)?.label ?? config.view
  const snippet = buildEmbedSnippet('your-collection-guid')
  return (
    <div className="mt-6 rounded-2xl border border-dashed bg-card/60 p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">What you&rsquo;re building</p>
      <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
        <RecapItem label="Source" value={sourceLabel} />
        <RecapItem label="Layout" value={layoutLabel} />
        <RecapItem label="Columns" value={`${config.columns} desktop · ${config.mobileColumns} mobile`} />
        <RecapItem label="Accent" value={config.accent} swatch={config.accent} />
      </ul>
      <p className="mt-4 text-xs text-muted-foreground">
        One line of HTML once it&rsquo;s live:{' '}
        <code className="break-all rounded bg-secondary px-1 py-0.5 text-[11px]">{snippet.split('\n')[0]}</code>
      </p>
      <a
        href={appSignupUrl({ source: config.source, ref: 'widget-builder-recap' })}
        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
      >
        See every feature in the app <ArrowRight className="h-3.5 w-3.5" aria-hidden />
      </a>
    </div>
  )
}

function RecapItem({ label, value, swatch }: { label: string; value: string; swatch?: string }) {
  return (
    <li className="flex items-center justify-between gap-3 rounded-lg border bg-background px-3 py-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
        {swatch && <span className="h-3.5 w-3.5 rounded-full border" style={{ backgroundColor: swatch }} aria-hidden />}
        {value}
      </span>
    </li>
  )
}
